/**
 * Sticky station selection for the tablet kiosk. A shop-floor tablet *is* a
 * station — the worker shouldn't re-pick "cutting vs stitching" every visit — so
 * the choice is remembered in localStorage and only the one-time picker (or the
 * corner "change" affordance) writes it. Scoped per company so one physical
 * tablet moved between workspaces doesn't leak a stale station.
 */
import { useCallback, useSyncExternalStore } from "react";
import type { wip as wipContract } from "@flint/api-contract";

const KEY = "flint.station";

function storageKey(companyId: string | null): string {
  return companyId ? `${KEY}.${companyId}` : KEY;
}

function subscribe(cb: () => void): () => void {
  window.addEventListener("storage", cb);
  return () => window.removeEventListener("storage", cb);
}

/**
 * Returns the remembered station for this workspace (or null) plus a setter.
 * `null` means "not yet chosen" → the caller shows the station picker.
 */
export function useStation(companyId: string | null): {
  station: wipContract.WipStage | null;
  setStation: (stage: wipContract.WipStage) => void;
  clearStation: () => void;
} {
  const key = storageKey(companyId);

  const station = useSyncExternalStore(
    subscribe,
    () => (localStorage.getItem(key) as wipContract.WipStage | null) ?? null,
    () => null,
  );

  const setStation = useCallback(
    (stage: wipContract.WipStage) => {
      localStorage.setItem(key, stage);
      // useSyncExternalStore only re-renders on cross-tab `storage` events, so
      // nudge the same-tab subscribers after a local write.
      window.dispatchEvent(new StorageEvent("storage", { key }));
    },
    [key],
  );

  const clearStation = useCallback(() => {
    localStorage.removeItem(key);
    window.dispatchEvent(new StorageEvent("storage", { key }));
  }, [key]);

  return { station, setStation, clearStation };
}
